import { Button, Modal, Typography } from 'antd'
import { useCallback, useEffect, useRef, useState } from 'react'

declare global {
  interface Window {
    build_info?: string
  }
}

const VERSION_CHECK_INTERVAL_MS = 5_000
const RELOAD_CACHE_BUSTER_PARAM = '__app_reload'

export const parseBuildInfo = (source: string): string | undefined => {
  const match = source.match(/build_info\s*=\s*['"]?([^'";\n]+)['"]?/)
  return match?.[1]?.trim()
}

const clearBrowserManagedCaches = async () => {
  const cleanupTasks: Promise<unknown>[] = []

  if ('caches' in window) {
    cleanupTasks.push(
      window.caches
        .keys()
        .then((cacheNames) => Promise.all(cacheNames.map((cacheName) => window.caches.delete(cacheName)))),
    )
  }

  if ('serviceWorker' in navigator) {
    cleanupTasks.push(
      navigator.serviceWorker
        .getRegistrations()
        .then((registrations) => Promise.all(registrations.map((registration) => registration.unregister()))),
    )
  }

  await Promise.all(cleanupTasks.map((task) => task.catch(() => undefined)))
}

export const AppVersionCheck = () => {
  const [updateRequired, setUpdateRequired] = useState(false)
  const [reloading, setReloading] = useState(false)
  const updateDetectedRef = useRef(false)
  const requestInFlightRef = useRef(false)

  const checkVersion = useCallback(async () => {
    if (updateDetectedRef.current || requestInFlightRef.current) {
      return
    }

    requestInFlightRef.current = true

    try {
      const response = await fetch(`/version.js?time=${Date.now()}`, {
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
        },
      })

      if (!response.ok) {
        return
      }

      const nextVersion = parseBuildInfo(await response.text())
      const currentVersion = window.build_info

      if (nextVersion && currentVersion && nextVersion !== currentVersion) {
        updateDetectedRef.current = true
        setUpdateRequired(true)
      }
    } catch {
      // Version check failures should not interrupt regular UI work.
    } finally {
      requestInFlightRef.current = false
    }
  }, [])

  useEffect(() => {
    void checkVersion()

    const intervalId = window.setInterval(() => void checkVersion(), VERSION_CHECK_INTERVAL_MS)
    const handleFocus = () => void checkVersion()
    const handlePageShow = () => void checkVersion()
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void checkVersion()
      }
    }

    window.addEventListener('focus', handleFocus)
    window.addEventListener('pageshow', handlePageShow)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      window.clearInterval(intervalId)
      window.removeEventListener('focus', handleFocus)
      window.removeEventListener('pageshow', handlePageShow)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [checkVersion])

  const handleReload = async () => {
    setReloading(true)

    try {
      await clearBrowserManagedCaches()
    } finally {
      const reloadUrl = new URL(window.location.href)
      reloadUrl.searchParams.set(RELOAD_CACHE_BUSTER_PARAM, Date.now().toString())
      window.location.replace(reloadUrl.toString())
    }
  }

  return (
    <Modal
      open={updateRequired}
      title="Update required"
      closable={false}
      maskClosable={false}
      keyboard={false}
      footer={
        <Button type="primary" loading={reloading} onClick={() => void handleReload()}>
          Reload
        </Button>
      }
    >
      <Typography.Paragraph>
        A new version of the console is available. Please reload the page to continue.
      </Typography.Paragraph>
      <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
        If the old version is still shown, use a hard reload: Ctrl+F5 or Ctrl+Shift+R on Windows/Linux, or Cmd+Shift+R on macOS.
      </Typography.Paragraph>
    </Modal>
  )
}
